import {
  HeadContent,
  Scripts,
  createRootRoute,
  useLocation,
} from '@tanstack/react-router'
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools'
import { TanStackDevtools } from '@tanstack/react-devtools'
import { useEffect, useState } from 'react'

import { checkAdminAccess } from '@rpc/admin-check.rpc'
import { authClient } from '@client/auth-client'
import {
  disableAmplitudeAnalytics,
  enableAmplitudeAnalytics,
  getAnalyticsRequestContext,
  setAnalyticsIdentity,
  trackAnalyticsEvent,
} from '@client/analytics'
import { getAuthSession } from '@rpc/auth-session.rpc'
import { syncAnalyticsIdentity } from '@rpc/analytics.rpc'

import googleSansLatinNormalUrl from '@fontsource-variable/google-sans/files/google-sans-latin-wght-normal.woff2?url'
import appCss from '../styles.css?url'
import { SpectrumAppProvider } from '../components/SpectrumAppProvider'
import { NotFound } from '../components/NotFound'

const COOKIE_CONSENT_STORAGE_KEY = 'buildingregs.cookie-consent.v1'
const GA_MEASUREMENT_ID =
  import.meta.env.VITE_GA_MEASUREMENT_ID ||
  process.env.VITE_GA_MEASUREMENT_ID ||
  'G-53CDHMCB9C'
const CLARITY_PROJECT_ID =
  import.meta.env.VITE_CLARITY_PROJECT_ID ||
  process.env.VITE_CLARITY_PROJECT_ID ||
  'wnlwx2tkw8'

export const Route = createRootRoute({
  loader: async () => ({
    authSession: await getAuthSession(),
  }),
  head: () => ({
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1',
      },
      {
        title: 'Building regs assistant',
      },
      {
        name: 'theme-color',
        media: '(prefers-color-scheme: light)',
        content: '#f8fafc',
      },
      {
        name: 'theme-color',
        media: '(prefers-color-scheme: dark)',
        content: '#090e1a',
      },
    ],
    links: [
      {
        rel: 'preload',
        href: googleSansLatinNormalUrl,
        as: 'font',
        type: 'font/woff2',
        crossOrigin: 'anonymous',
      },
      {
        rel: 'stylesheet',
        href: appCss,
      },
    ],
    scripts: [
      {
        children:
          "(() => { const root = document.documentElement; const supportsMatchMedia = typeof window.matchMedia === 'function'; const dark = supportsMatchMedia ? window.matchMedia('(prefers-color-scheme: dark)').matches : false; root.classList.toggle('dark', dark); root.style.colorScheme = dark ? 'dark' : 'light'; root.setAttribute('data-color-scheme', dark ? 'dark' : 'light'); })();",
      },
    ],
  }),

  shellComponent: RootDocument,
  notFoundComponent: NotFound,
})

function RootDocument({ children }: { children: React.ReactNode }) {
  const showDevtools =
    process.env.NODE_ENV !== 'production' &&
    (import.meta.env.VITE_SHOW_DEVTOOLS === 'true' ||
      process.env.VITE_SHOW_DEVTOOLS === 'true')

  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        <ThemeSync />
        <AnalyticsBootstrap />
        <GoogleAnalyticsPageView />
        <SpectrumAppProvider>{children}</SpectrumAppProvider>
        <CookieConsentPrompt />
        {showDevtools ? (
          <TanStackDevtools
            config={{
              position: 'bottom-right',
            }}
            plugins={[
              {
                name: 'TanStack router',
                render: <TanStackRouterDevtoolsPanel />,
              },
            ]}
          />
        ) : null}
        <Scripts />
      </body>
    </html>
  )
}

function GoogleAnalyticsPageView() {
  const location = useLocation()
  const { data: session, isPending } = authClient.useSession()
  const { isAdmin, resolved: adminResolved } = useAnalyticsAdminState(
    session,
    isPending,
  )

  useEffect(() => {
    if (!isAnalyticsEnabledInBrowser()) return
    if (isPending || !adminResolved || isAdmin) return
    let sent = false
    const sendPageView = () => {
      if (sent) return
      const gtag = (window as Window & { gtag?: (...args: Array<any>) => void })
        .gtag
      if (!gtag) return

      sent = true
      trackAnalyticsEvent('page_view', {
        page_path: location.href,
        page_location: window.location.href,
        page_title: document.title,
      })
    }

    sendPageView()
    window.addEventListener('buildingregs:google-analytics-ready', sendPageView)

    return () => {
      window.removeEventListener(
        'buildingregs:google-analytics-ready',
        sendPageView,
      )
    }
  }, [adminResolved, isAdmin, isPending, location.href])

  return null
}

function AnalyticsBootstrap() {
  const { data: session, isPending } = authClient.useSession()
  const analyticsSession = session as {
    user?: {
      id?: string
      billingPlan?: string | null
      activeOrganizationId?: string | null
      persona?: string | null
    }
    session?: { activeOrganizationId?: string | null }
  } | null
  const { isAdmin, resolved: adminResolved } = useAnalyticsAdminState(
    session,
    isPending,
  )

  useEffect(() => {
    if (!isAnalyticsEnabledInBrowser()) return
    if (isPending || !adminResolved) return
    if (isAdmin) {
      disableAnalyticsForCurrentPage()
      return
    }
    installDefaultAnalytics()
    const userId = analyticsSession?.user?.id ?? null
    const identityContext = {
      planId:
        analyticsSession?.user?.billingPlan ??
        (analyticsSession?.user ? 'registeredUser' : 'anonymous'),
      organizationId:
        analyticsSession?.session?.activeOrganizationId ??
        analyticsSession?.user?.activeOrganizationId ??
        null,
      persona: analyticsSession?.user?.persona ?? null,
    }
    void setAnalyticsIdentity(userId, identityContext)
    void getAnalyticsRequestContext()
      .then((data) => syncAnalyticsIdentity({ data }))
      .then((organizationContext) =>
        setAnalyticsIdentity(userId, {
          ...identityContext,
          organizationRole: organizationContext.organizationRole ?? null,
          organizationSizeBand:
            organizationContext.organizationSizeBand ?? null,
        }),
      )
      .catch(() => undefined)
  }, [
    adminResolved,
    isAdmin,
    isPending,
    analyticsSession?.session?.activeOrganizationId,
    analyticsSession?.user?.activeOrganizationId,
    analyticsSession?.user?.billingPlan,
    analyticsSession?.user?.id,
    analyticsSession?.user?.persona,
  ])

  return null
}

function CookieConsentPrompt() {
  const location = useLocation()
  const { data: session, isPending } = authClient.useSession()
  const { isAdmin, resolved: adminResolved } = useAnalyticsAdminState(
    session,
    isPending,
  )
  const [choice, setChoice] = useState<'loading' | 'granted' | 'denied' | null>(
    'loading',
  )
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false)

  useEffect(() => {
    const enabled = isAnalyticsEnabledInBrowser()
    setAnalyticsEnabled(enabled)
    if (!enabled) {
      setChoice('granted')
      return
    }
    setChoice(readCookieConsent())
  }, [])

  useEffect(() => {
    if (!analyticsEnabled) return
    if (isPending || !adminResolved) return

    if (isAdmin) {
      disableAnalyticsForCurrentPage()
      setChoice('denied')
      return
    }

    if (!session?.user) return

    try {
      window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, 'granted')
    } catch (_) {
      // Ignore storage failures; logged-in consent still applies for this page.
    }
    setChoice('granted')
  }, [adminResolved, analyticsEnabled, isAdmin, isPending, session])

  useEffect(() => {
    if (!analyticsEnabled) return
    if (isPending || !adminResolved) return
    if (isAdmin) {
      disableAnalyticsForCurrentPage()
      return
    }
    if (choice === 'granted') {
      updateGoogleConsent('granted')
      loadMicrosoftClarity()
      void enableAmplitudeAnalytics()
    }

    if (choice === 'denied') {
      updateGoogleConsent('denied')
      disableAmplitudeAnalytics()
      removeMicrosoftClarity()
    }
  }, [adminResolved, analyticsEnabled, choice, isAdmin, isPending])

  const choose = (nextChoice: 'granted' | 'denied') => {
    try {
      window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, nextChoice)
    } catch (_) {
      // Ignore storage failures; consent mode is still updated for this page.
    }
    setChoice(nextChoice)
  }

  const waitingForSignedInAdminResolution =
    Boolean(session?.user) && !adminResolved

  if (
    !analyticsEnabled ||
    choice !== null ||
    waitingForSignedInAdminResolution
  ) {
    return null
  }

  const isChatRoute =
    location.pathname === '/chat' || location.href.startsWith('/chat')

  return (
    <div
      className={
        isChatRoute
          ? 'pointer-events-none fixed inset-x-4 bottom-[calc(env(safe-area-inset-bottom)+10.75rem)] z-40 flex justify-center sm:bottom-5'
          : 'pointer-events-none fixed inset-x-4 bottom-[calc(env(safe-area-inset-bottom)+1rem)] z-40 flex justify-center sm:bottom-5'
      }
      data-testid="cookie-consent-prompt"
    >
      <div className="pointer-events-auto flex max-w-[min(100%,28rem)] items-center gap-3 rounded-full border border-neutral-200/80 bg-white/95 px-4 py-2.5 text-xs leading-snug text-neutral-600 shadow-[0_14px_34px_rgba(15,23,42,0.16)] backdrop-blur dark:border-neutral-800/80 dark:bg-neutral-950/90 dark:text-neutral-300 dark:shadow-[0_14px_34px_rgba(0,0,0,0.42)]">
        <span className="min-w-0">
          We use analytics cookies to improve the service.
        </span>
        <button
          type="button"
          className="shrink-0 rounded-full px-2.5 py-1.5 text-xs font-medium text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-100"
          onClick={() => choose('denied')}
        >
          Decline
        </button>
        <button
          type="button"
          className="shrink-0 rounded-full bg-neutral-950 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-neutral-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 dark:bg-neutral-100 dark:text-neutral-950 dark:hover:bg-white"
          onClick={() => choose('granted')}
        >
          Accept
        </button>
      </div>
    </div>
  )
}

function readCookieConsent() {
  try {
    const stored = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY)
    return stored === 'granted' || stored === 'denied' ? stored : null
  } catch (_) {
    return null
  }
}

function isAdminSession(session: unknown) {
  if (!session || typeof session !== 'object') {
    return false
  }

  const authSession = session as {
    user?: { role?: unknown }
    session?: { impersonatedBy?: unknown }
  }

  return (
    authSession.user?.role === 'admin' ||
    (typeof authSession.session?.impersonatedBy === 'string' &&
      authSession.session.impersonatedBy.length > 0)
  )
}

function useAnalyticsAdminState(session: unknown, isPending: boolean) {
  const [state, setState] = useState<'unknown' | 'admin' | 'non-admin'>(() => {
    if (!session || typeof session !== 'object' || !(session as any).user) {
      return 'non-admin'
    }
    return isAdminSession(session) ? 'admin' : 'unknown'
  })

  useEffect(() => {
    let cancelled = false

    if (isPending) {
      return () => {
        cancelled = true
      }
    }

    if (!session || typeof session !== 'object' || !(session as any).user) {
      setState('non-admin')
      return () => {
        cancelled = true
      }
    }

    if (isAdminSession(session)) {
      setState('admin')
      return () => {
        cancelled = true
      }
    }

    setState('unknown')

    void checkAdminAccess()
      .then((hasAdminAccess) => {
        if (cancelled) return
        setState(hasAdminAccess ? 'admin' : 'non-admin')
      })
      .catch(() => {
        if (cancelled) return
        // Fail closed for signed-in sessions so admins are never tracked
        // because of an access-check error.
        setState('admin')
      })

    return () => {
      cancelled = true
    }
  }, [
    isPending,
    session && typeof session === 'object'
      ? (session as any).session?.id
      : null,
    session && typeof session === 'object'
      ? (session as any).session?.token
      : null,
    session && typeof session === 'object'
      ? (session as any).session?.impersonatedBy
      : null,
    session && typeof session === 'object' ? (session as any).user?.id : null,
    session && typeof session === 'object' ? (session as any).user?.role : null,
  ])

  return {
    isAdmin: state === 'admin',
    resolved: state !== 'unknown',
  }
}

function updateGoogleConsent(consent: 'granted' | 'denied') {
  if (!isAnalyticsEnabledInBrowser()) return
  const gtag = (window as Window & { gtag?: (...args: Array<any>) => void })
    .gtag
  if (!gtag) return

  gtag('consent', 'update', {
    ad_storage: consent,
    ad_user_data: consent,
    ad_personalization: consent,
    analytics_storage: consent,
  })
}

function loadMicrosoftClarity() {
  if (!isAnalyticsEnabledInBrowser()) return
  const windowWithClarity = window as Window & {
    clarity?: (...args: Array<any>) => void
  }
  windowWithClarity.clarity =
    windowWithClarity.clarity ||
    function clarityQueue(...args: Array<any>) {
      ;(windowWithClarity.clarity as any).q =
        (windowWithClarity.clarity as any).q || []
      ;(windowWithClarity.clarity as any).q.push(args)
    }

  updateMicrosoftClarityConsent('granted')

  if (document.querySelector('script[data-ms-clarity="true"]')) return

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`
  script.dataset.msClarity = 'true'
  document.head.appendChild(script)
}

function disableAnalyticsForCurrentPage() {
  updateGoogleConsent('denied')
  updateMicrosoftClarityConsent('denied')
  disableAmplitudeAnalytics()
  removeMicrosoftClarity()
  removeGoogleAnalyticsScript()
}

function updateMicrosoftClarityConsent(consent: 'granted' | 'denied') {
  const clarity = (
    window as Window & { clarity?: (...args: Array<any>) => void }
  ).clarity
  if (!clarity) return

  clarity('consentv2', {
    ad_Storage: consent,
    analytics_Storage: consent,
  })

  if (consent === 'denied') {
    clarity('consent', false)
  }
}

function removeMicrosoftClarity() {
  updateMicrosoftClarityConsent('denied')
  document.head
    .querySelectorAll('script[data-ms-clarity="true"]')
    .forEach((script) => script.remove())
}

function removeGoogleAnalyticsScript() {
  document.head
    .querySelectorAll(
      `script[src="https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}"]`,
    )
    .forEach((script) => script.remove())
  ;(
    window as Window & {
      __buildingRegsGoogleAnalyticsInitialized?: boolean
    }
  ).__buildingRegsGoogleAnalyticsInitialized = false
}

function installGoogleAnalytics() {
  const windowWithAnalytics = window as Window & {
    dataLayer?: Array<any>
    gtag?: (...args: Array<any>) => void
    __buildingRegsGoogleAnalyticsInitialized?: boolean
  }
  if (windowWithAnalytics.__buildingRegsGoogleAnalyticsInitialized) return

  windowWithAnalytics.dataLayer = windowWithAnalytics.dataLayer || []
  windowWithAnalytics.gtag =
    windowWithAnalytics.gtag ||
    function gtag(...args: Array<any>) {
      windowWithAnalytics.dataLayer?.push(args)
    }

  const cookieConsent = readCookieConsent() === 'denied' ? 'denied' : 'granted'
  windowWithAnalytics.gtag('consent', 'default', {
    ad_storage: cookieConsent,
    ad_user_data: cookieConsent,
    ad_personalization: cookieConsent,
    analytics_storage: cookieConsent,
  })
  windowWithAnalytics.gtag('set', 'ads_data_redaction', true)
  windowWithAnalytics.gtag('js', new Date())
  windowWithAnalytics.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false,
  })

  if (
    !document.querySelector(
      `script[src="https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}"]`,
    )
  ) {
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
    document.head.appendChild(script)
  }

  windowWithAnalytics.__buildingRegsGoogleAnalyticsInitialized = true
  window.dispatchEvent(new Event('buildingregs:google-analytics-ready'))
}

function installDefaultAnalytics() {
  if (readCookieConsent() === 'denied') {
    disableAmplitudeAnalytics()
    removeMicrosoftClarity()
    installGoogleAnalytics()
    return
  }

  loadMicrosoftClarity()
  installGoogleAnalytics()
  void enableAmplitudeAnalytics()
}

function isAnalyticsEnabledInBrowser() {
  if (typeof window === 'undefined') return false
  if (analyticsForceEnabled()) return true
  if (analyticsDisabled()) return false
  if (import.meta.env.DEV || process.env.NODE_ENV !== 'production') {
    return false
  }

  const hostname = window.location.hostname.toLowerCase()
  return !isLocalHostname(hostname)
}

function analyticsForceEnabled() {
  return (
    import.meta.env.VITE_FORCE_ANALYTICS === 'true' ||
    process.env.VITE_FORCE_ANALYTICS === 'true'
  )
}

function analyticsDisabled() {
  return (
    import.meta.env.VITE_DISABLE_ANALYTICS === 'true' ||
    process.env.VITE_DISABLE_ANALYTICS === 'true'
  )
}

function isLocalHostname(hostname: string) {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '::1' ||
    hostname.endsWith('.localhost') ||
    hostname.endsWith('.local')
  )
}

function ThemeSync() {
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return
    }

    const root = document.documentElement
    const media = window.matchMedia('(prefers-color-scheme: dark)')

    const applyTheme = (isDark: boolean) => {
      root.classList.toggle('dark', isDark)
      root.style.colorScheme = isDark ? 'dark' : 'light'
      root.setAttribute('data-color-scheme', isDark ? 'dark' : 'light')
    }

    applyTheme(media.matches)
    const onChange = (event: MediaQueryListEvent) => applyTheme(event.matches)
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])

  return null
}

export const __rootTestUtils = {
  analyticsDisabled,
  analyticsForceEnabled,
  installGoogleAnalytics,
  isAdminSession,
  isAnalyticsEnabledInBrowser,
  isLocalHostname,
  loadMicrosoftClarity,
  updateGoogleConsent,
}
